import {
  PRODUCT_GROUP_URL_KEY,
  PRODUCT_POSITION_URL_KEY,
} from '@/pages/admin/venue/[venueName]/p/new'
import { PRODUCT_GROUP_POSITION_URL_KEY } from '@/pages/admin/venue/[venueName]/pg/new'
import { VenuePageViewMode } from '@/pages/venue/[venueName]'
import {
  SCROLL_TO_MODIFIERS_QUERY_PARAM,
  SOURCE_PRODUCT_GROUP_NAME,
} from '@/pages/venue/[venueName]/p/[productGroupName]/[productName]'
import { useAppContext } from '@/pages/_app'
import {
  ProductGroupAdaptable,
  PreferenceMode,
  useSelectedPreferences,
} from '@/services/preferences'
import { saveProductOrder } from '@/services/product'
import { saveProductGroupOrder } from '@/services/product-group'
import { createProductGroupIntersectionObserver } from '@/services/product-group-intersection-observer'
import { useVenueContext } from '@/services/venue'
import { uiEvents } from '@/utils/event'
import { Product as TProduct, ProductGroup as TProductGroup, Venue } from 'shared/types/api'
import { sum } from 'shared/utils/array'
import { useRouter } from 'next/router'
import React, { useRef, useEffect, useState, Fragment } from 'react'
import partition from 'lodash/partition'
import { useConstant } from '@/utils/use-constant'
import scrollTo from 'animated-scroll-to'
import Link from 'next/link'
import { AdminProductButton } from './admin-product-button'
import { ArrowIcon } from './arrow'
import { PencilIcon } from './pencil-icon'
import { Product } from './product'
import { ProductGroup } from './product-group'
import { ProductModalMenu } from './product-modal-menu'
import { ThreeDotsIcon } from './three-dots-icon'
import { Tooltip } from './tooltip'
import { NewEntityLine } from './new-entity-line'
import { ProductGroupNavigatorOverlay } from '@/components/product-group-navigator'

declare global {
  interface AppUIEventMap {
    ProductBlockRendered: {
      type: 'ProductBlockRendered'
    }
  }
}

export default function ProductsBlock({
  productGroups,
  viewMode,
  preferenceMode,
  venue,
  setVenueOverriden,
}: {
  productGroups: ProductGroupAdaptable[]
  viewMode: VenuePageViewMode
  preferenceMode: PreferenceMode
  venue: Venue
  setVenueOverriden: (venue: Venue) => void
}) {
  const { tracker, apiClient, t } = useAppContext()
  const router = useRouter()
  const venueContext = useVenueContext()!
  const { venueName, orderManager } = venueContext
  const { basket, deliveryMapManager } = orderManager

  const { requestDeliveryAddressIfNeeded } = deliveryMapManager

  const showTwoProductsInRow = venue.features.showTwoProductsInRow

  useEffect(() => {
    // we need setTimeout to make sure that the event gets emitted after the subscription is defined
    setTimeout(() => {
      uiEvents.next({ type: 'ProductBlockRendered' })
    }, 1)
  }, [])

  const intersectionObserver = useConstant(() => {
    return createProductGroupIntersectionObserver()
  })

  const scheduledScrollRef = useRef<number | null>(null)
  useEffect(() => {
    if (!scheduledScrollRef.current) {
      return
    }
    scrollTo(scheduledScrollRef.current)
    // eslint-disable-next-line immutable/no-mutation
    scheduledScrollRef.current = null
  })

  const moveProductGroup = (productGroup: TProductGroup, direction: 'up' | 'down') => {
    const venueCopy: Venue = JSON.parse(JSON.stringify(venue))
    const productGroupIndex = venueCopy.productGroups.findIndex((p) => {
      return p.name === productGroup.name
    })
    const currentProductGroup = venueCopy.productGroups[productGroupIndex]!
    const productGroupToSwapIndex =
      direction === 'up' ? productGroupIndex - 1 : productGroupIndex + 1
    const productGroupToSwap = venueCopy.productGroups[productGroupToSwapIndex]!

    // mutable by design
    // eslint-disable-next-line immutable/no-mutation
    venueCopy.productGroups[productGroupIndex] = productGroupToSwap
    // mutable by design
    // eslint-disable-next-line immutable/no-mutation
    venueCopy.productGroups[productGroupToSwapIndex] = currentProductGroup

    const productGroupWrapper = document.getElementById(`pg-${currentProductGroup.name}`)!

    const productGroupToSwapWrapper = document.getElementById(`pg-${productGroupToSwap.name}`)!

    const heightDiff = productGroupWrapper.offsetHeight - productGroupToSwapWrapper.offsetHeight

    // eslint-disable-next-line immutable/no-mutation
    scheduledScrollRef.current =
      direction === 'down'
        ? productGroupToSwapWrapper.offsetTop - heightDiff - 150
        : productGroupToSwapWrapper.offsetTop - 150

    saveProductGroupOrder(
      apiClient,
      venueName,
      venueCopy.productGroups
        .filter((pg) => {
          return pg.kind === 'internal'
        })
        .map((pg) => {
          return pg.name
        }),
    )
      .then(() => {
        return venueContext.refreshVenue()
      })
      .catch((e) => {
        return tracker.trackError(e)
      })

    return venueCopy
  }

  const moveProduct = (
    productGroup: TProductGroup,
    product: TProduct,
    direction: 'up' | 'down',
  ) => {
    const venueCopy: Venue = JSON.parse(JSON.stringify(venue))
    const currentProductGroup = venueCopy.productGroups.find((pg) => {
      return pg.name === productGroup.name
    })!
    const productIndex = currentProductGroup.products.findIndex((p) => {
      return p.name === product.name
    })
    const currentProduct = currentProductGroup.products[productIndex]!

    const productToSwapIndex = direction === 'up' ? productIndex - 1 : productIndex + 1
    const productToSwap = currentProductGroup.products[productToSwapIndex]!

    // mutable by design
    // eslint-disable-next-line immutable/no-mutation
    currentProductGroup.products[productIndex] = productToSwap

    // mutable by design
    // eslint-disable-next-line immutable/no-mutation
    currentProductGroup.products[productToSwapIndex] = currentProduct

    const productWrapper = document.getElementById(`product-${productGroup.name}-${product.name}`)!

    const productToSwapWrapper = document.getElementById(
      `product-${productGroup.name}-${productToSwap.name}`,
    )!

    const heightDiff = productWrapper.offsetHeight - productToSwapWrapper.offsetHeight

    // eslint-disable-next-line immutable/no-mutation
    scheduledScrollRef.current =
      direction === 'down'
        ? productToSwapWrapper.offsetTop - heightDiff - 150
        : productToSwapWrapper.offsetTop - 150

    saveProductOrder(
      apiClient,
      venueName,
      productGroup.name,
      currentProductGroup.products.map((p) => {
        return p.name
      }),
    )
      .then(() => {
        return venueContext.refreshVenue()
      })
      .catch((e) => {
        return tracker.trackError(e)
      })

    return venueCopy
  }

  const [productOverriddenProperties, setProductOverriddenProperties] = useState<{
    [key: string]: Partial<TProduct>
  }>({})

  const selectedPreferences = useSelectedPreferences()

  const firstProductGroupIdx = productGroups.findIndex((pg) => {
    return pg.kind !== 'virtual'
  })

  return (
    <div id="product-groups-section">
      <ProductGroupNavigatorOverlay productGroups={productGroups} />
      {productGroups.map((productGroup, idx) => {
        const isLatestProductGroup = idx === productGroups.length - 1
        const isFirstProductGroup = idx === firstProductGroupIdx

        const [adaptedProducts, notAdaptedProducts] = partition(productGroup.products, (p) => {
          return p.fitDescription.productFitsPreferences
        })

        const products =
          preferenceMode === 'filter'
            ? adaptedProducts
            : [...adaptedProducts, ...notAdaptedProducts]

        return (
          <Fragment key={productGroup.name}>
            {viewMode === 'adminProductGroups' &&
              productGroup.kind === 'internal' &&
              venue.posType === 'local' && (
                <Link
                  href={`/admin/venue/${venueName}/pg/new?${PRODUCT_GROUP_POSITION_URL_KEY}=${
                    idx - firstProductGroupIdx
                  }`}
                >
                  <NewEntityLine />
                </Link>
              )}
            {viewMode === 'adminProductGroups' && productGroup.kind === 'internal' && (
              <div className="px-4 pb-2 flex justify-between max-w-webAppMobileWidth mx-auto">
                <div className="flex">
                  {!isFirstProductGroup && (
                    <div className="mr-2">
                      <AdminProductButton
                        onClick={() => {
                          setVenueOverriden(moveProductGroup(productGroup, 'up'))
                        }}
                      >
                        <div style={{ transform: 'rotate(180deg)' }}>
                          <ArrowIcon />
                        </div>
                      </AdminProductButton>
                    </div>
                  )}
                  {!isLatestProductGroup && (
                    <div>
                      <AdminProductButton
                        onClick={() => {
                          setVenueOverriden(moveProductGroup(productGroup, 'down'))
                        }}
                      >
                        <ArrowIcon />
                      </AdminProductButton>
                    </div>
                  )}
                </div>
                <div className="flex">
                  <div className="ml-2">
                    <Link href={`/venue/${venueName}/pg/${productGroup.name}`}>
                      <AdminProductButton>
                        <PencilIcon />
                      </AdminProductButton>
                    </Link>
                  </div>
                </div>
              </div>
            )}
            <ProductGroup data={productGroup} intersectionObserver={intersectionObserver}>
              {(product, idx) => {
                if (viewMode === 'adminProductGroups') {
                  return null
                }
                const isFirstProduct = idx === 0
                const isLastProduct = idx === products.length - 1
                const isSecondToLastProduct = idx === products.length - 2
                const productGroupName = product.productGroupName
                const productName = product.name

                return (
                  <div className="mx-auto flex flex-col h-full max-w-webAppMobileWidth">
                    {viewMode === 'adminProducts' &&
                    productGroup.kind === 'internal' &&
                    venue.posType === 'local' ? (
                      <Link
                        passHref={true}
                        href={`/admin/venue/${venueName}/p/new?${PRODUCT_GROUP_URL_KEY}=${productGroupName}&${PRODUCT_POSITION_URL_KEY}=${idx}`}
                      >
                        <NewEntityLine />
                      </Link>
                    ) : (
                      !isFirstProduct &&
                      viewMode === 'adminProducts' && <div className="mt-12"></div>
                    )}
                    <div className="h-4">{/* gap */}</div>
                    {viewMode === 'adminProducts' &&
                      ['internal', 'virtual'].includes(productGroup.kind) && (
                        <div className="px-4 pb-2 flex justify-between">
                          {productGroup.kind === 'internal' ? (
                            <div className="flex">
                              {!isFirstProduct && (
                                <div className="mr-2">
                                  <AdminProductButton
                                    onClick={() => {
                                      setVenueOverriden(moveProduct(productGroup, product, 'up'))
                                    }}
                                  >
                                    <div style={{ transform: 'rotate(180deg)' }}>
                                      <ArrowIcon />
                                    </div>
                                  </AdminProductButton>
                                </div>
                              )}
                              {!isLastProduct && (
                                <div>
                                  <AdminProductButton
                                    onClick={() => {
                                      setVenueOverriden(moveProduct(productGroup, product, 'down'))
                                    }}
                                  >
                                    <ArrowIcon />
                                  </AdminProductButton>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div></div>
                          )}
                          <div className="flex">
                            {
                              <div className="ml-2">
                                {productGroup.kind !== 'virtual' && (
                                  <ProductModalMenu
                                    product={product}
                                    setProductOverriddenProperties={setProductOverriddenProperties}
                                  />
                                )}
                                <a
                                  onClick={() => {
                                    uiEvents.next({
                                      type: 'ProductModalMenuClick',
                                      product,
                                    })
                                  }}
                                >
                                  <AdminProductButton>
                                    <ThreeDotsIcon />
                                  </AdminProductButton>
                                </a>
                              </div>
                            }

                            <div className="ml-2">
                              <Link
                                href={`/venue/${venueName}/p/${productGroupName}/${product.name}?edit=1&${SOURCE_PRODUCT_GROUP_NAME}=${productGroup.name}`}
                              >
                                <AdminProductButton>
                                  <PencilIcon />
                                </AdminProductButton>
                              </Link>
                            </div>
                          </div>
                        </div>
                      )}
                    <Product
                      filtersUsed={selectedPreferences.hasPreferences}
                      product={{
                        ...product,
                        ...productOverriddenProperties[`${productGroupName}/${productName}`],
                      }}
                      sourceProductGroupName={productGroup.name}
                      venueName={venueName}
                      orderedQuantity={sum(
                        basket.products
                          .filter({
                            productGroupName,
                            productName,
                          })
                          .map((p) => {
                            return p.quantity
                          }),
                      )}
                      onQuantityChange={(quantity, change, e) => {
                        // Request delivery address if a customer hasn't entered the delivery address yet
                        if (requestDeliveryAddressIfNeeded(e.target) === 'requested') {
                          return
                        }

                        if (product.modifierGroups.length > 0) {
                          if (change === '+') {
                            return router.push(
                              `/venue/${venueName}/p/${productGroupName}/${product.name}?${SCROLL_TO_MODIFIERS_QUERY_PARAM}&${SOURCE_PRODUCT_GROUP_NAME}=${productGroup.name}`,
                            )
                          }

                          const latestBasketProduct = basket.products
                            .filter({
                              productGroupName,
                              productName,
                            })
                            .filter((p) => {
                              return p.quantity > 0
                            })
                            .sort((a, b) => {
                              return b.createdAt - a.createdAt
                            })[0]

                          if (latestBasketProduct) {
                            basket.products.set(latestBasketProduct.__id, {
                              quantity: latestBasketProduct.quantity - 1,
                            })
                          }

                          return
                        }
                        basket.products.setQuantity(
                          {
                            productGroupName,
                            productName,
                            modifiers: {},
                          },
                          quantity,
                        )
                      }}
                      allowMovePriceToOrderButton={true}
                    />
                    {!isLastProduct && viewMode === 'visitor' ? (
                      <div
                        className={`px-4 ${
                          isSecondToLastProduct && products.length % 2 === 0
                            ? showTwoProductsInRow
                              ? 'hidden'
                              : 'lg:hidden'
                            : ''
                        }`}
                      >
                        <div className="my-4 border-b border-ghost dark:border-ghostDark">
                          {/* horizontal separator */}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )
              }}
            </ProductGroup>
            {viewMode === 'adminProducts' &&
            productGroup.kind === 'internal' &&
            venue.posType === 'local' ? (
              <>
                <Link
                  href={`/admin/venue/${venueName}/p/new?${PRODUCT_GROUP_URL_KEY}=${productGroup.name}&${PRODUCT_POSITION_URL_KEY}=${productGroup.products.length}`}
                  legacyBehavior
                >
                  <div>
                    {productGroup.products.length === 0 && (
                      <div className="relative flex justify-center top-8">
                        <Tooltip arrow={{ facing: 'down' }}>
                          {t('venueName:pressToAddItem')}
                        </Tooltip>
                      </div>
                    )}
                    <NewEntityLine />
                  </div>
                </Link>
                <div className="h-4">{/* gap */}</div>
              </>
            ) : (
              !isLatestProductGroup &&
              viewMode === 'adminProductGroups' && <div className="mt-16"></div>
            )}
            {(() => {
              if (viewMode === 'adminProductGroups') {
                return <div className="my-4">{/* horizontal separator */}</div>
              }

              if (!isLatestProductGroup) {
                return (
                  <div className="my-4 border-4 border-backScreen dark:border-backScreenDark">
                    {/* horizontal separator */}
                  </div>
                )
              }

              return null
            })()}
          </Fragment>
        )
      })}
      {viewMode === 'adminProductGroups' && venue.posType === 'local' && (
        <>
          <Link
            href={`/admin/venue/${venueName}/pg/new?${PRODUCT_POSITION_URL_KEY}=${productGroups.length}`}
            legacyBehavior
          >
            <div>
              {productGroups.length === 0 && (
                <div className="relative flex justify-center top-8">
                  <Tooltip arrow={{ facing: 'down' }}>{t('venueName:pressToAddCategory')}</Tooltip>
                </div>
              )}
              <NewEntityLine />
            </div>
          </Link>
          <div className="h-4">{/* gap */}</div>
        </>
      )}
    </div>
  )
}
